import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions, useMe } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { WineCarouselLoader } from '@/components/ui/wine-carousel-loader';
import { Plus, Settings, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { EventDataTable } from './EventDataTable';
import { EventFilters, type EventSort } from './EventFilters';
import { Pagination } from './Pagination';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { EventFormDialog, DialogMode } from './EventFormDialog';
import { RefundWizardDialog } from './RefundWizardDialog';
import { ParticipantsDialog } from './ParticipantsDialog';
import { sortDepots } from './depot-format';
import {
  useEvents,
  useEventTypes,
  useDepots,
  useCreateEvent,
  useUpdateEvent,
  useSubmitEvent,
  useDeleteEvent,
  useApproveEvent,
  useRejectEvent,
  useReopenEvent,
  useCancelEvent,
  useCompleteEvent,
  useNotifyAdvertisingDepots,
} from '@/services/event-service';
import {
  DepotEvent,
  DepotRef,
  EventFilters as Filters,
  EventListParams,
  Role,
  EventStatus,
  EventOrigin,
  CreateEventRequest,
} from '@/types';

/** App-Icon-artige Zähl-Bubble für die Schnellzugriff-Buttons (oben rechts am Button).
 *  Bei 0 ausgeblendet; ab 100 als "99+". */
function CountBubble({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-xs font-semibold leading-none text-white ring-2 ring-background">
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function EventListPage() {
  const { t } = useTranslation('events');
  // Not useAuth(): that context only ever carries the raw MSAL account (see
  // AuthContext.tsx — role/depots there are a standing "would come from token
  // claims in production" TODO, always undefined). /api/me is the one place
  // that actually resolves role and depots (Entra group, commercetools,
  // allowlist, or an assumed role from the staging switcher) — same source the
  // switcher itself and usePermissions() already rely on.
  const { data: me } = useMe();
  const role = me?.role as Role | undefined;
  const isPartner = role === Role.Partner || role === Role.Mitarbeiter;
  // Same right as EventTextListPage.tsx's/CancellationTermListPage.tsx's
  // `canManage` (US-203) — the settings page itself is right-based, not
  // role-based, so the nav link to it must be too (29.09.2026 fix: this used
  // to hide behind `isPartner`, so a partner holding either template edit
  // right had no way to reach the settings page at all).
  const canManageEventSettings =
    (me?.permissions?.eventTexts ?? []).includes('edit') ||
    (me?.permissions?.cancellationTerms ?? []).includes('edit');
  const { toast } = useToast();
  const perms = usePermissions('events');

  // refundStatus ist bewusst nicht Teil des kanonischen EventFilters-Typs
  // (siehe types/index.ts) — nur hier lokal erweitert, damit der
  // "Abrechnung zu prüfen"-Schnellzugriff ihn wie jeden anderen Filter über
  // dasselbe State-Objekt transportieren kann (inkl. "Filter zurücksetzen").
  const [filters, setFilters] = useState<Filters & { refundStatus?: string[]; lowContingent?: boolean }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // "Filter zurücksetzen" (25.09.2026, Nutzervorgabe: in die Schnellzugriff-
  // Zeile verschoben, daher hier statt in EventFilters.tsx) — refundStatus/
  // lowContingent mit eingeschlossen, obwohl EventFilters.tsx den
  // kanonischen Filtertyp nicht kennt: sonst würde ein per Schnellzugriff
  // gesetzter Wert den Button unsichtbar lassen bzw. beim Klick nicht mit
  // zurückgesetzt.
  const hasActiveFilters =
    !!filters.search ||
    (filters.status?.length ?? 0) > 0 ||
    (filters.audience?.length ?? 0) > 0 ||
    (filters.depotKeys?.length ?? 0) > 0 ||
    (filters.vaTypes?.length ?? 0) > 0 ||
    (filters.refundStatus?.length ?? 0) > 0 ||
    !!filters.lowContingent ||
    !!filters.dateFrom ||
    !!filters.dateTo;
  const clearAllFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };
  // Ohne explizite Auswahl (15.09.2026): der Backend-Standard greift
  // (Depot/Datum/VA-Typ/Titel, siehe sortEvents()) — vorher wurde hier
  // IMMER 'createdAt'/'desc' fest gesendet, unabhängig vom Backend-Default.
  const [sort, setSort] = useState<EventSort>({});

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>('create');
  const [activeEvent, setActiveEvent] = useState<DepotEvent | null>(null);
  /** Event awaiting delete confirmation; null closes the dialog. */
  const [pendingDelete, setPendingDelete] = useState<DepotEvent | null>(null);
  /**
   * Just-approved event awaiting the "notify advertising depots?" confirmation;
   * null closes the dialog. Set right after a successful approval, not as part
   * of the approve transition itself — declining or failing the notification
   * must never affect the approval, which has already happened.
   */
  const [pendingAdvertisingNotify, setPendingAdvertisingNotify] = useState<DepotEvent | null>(null);
  /**
   * Prefill for a copy — a create with the source event's data.
   *
   * Kept separate from activeEvent: passing the source event as `event` would put
   * the dialog into edit mode and overwrite the original on save.
   */
  const [copySource, setCopySource] = useState<DepotEvent | null>(null);
  /** Nicht-null = Teilnehmerliste offen für diese VA. */
  const [refundFor, setRefundFor] = useState<DepotEvent | null>(null);
  const [participantsFor, setParticipantsFor] = useState<DepotEvent | null>(null);

  const params: EventListParams = { ...filters, page: currentPage, pageSize, ...sort };
  const { data, isLoading, isError, error } = useEvents(params);
  const { data: eventTypes = [] } = useEventTypes();
  const { data: allDepots = [] } = useDepots();

  // Schnellzugriff-Zähler für Admin/Zentrale (21.09.2026-Follow-up) — eigene,
  // vom aktuellen Filter unabhängige Abfragen, nur pageSize=1 (der Bedarf ist
  // nur `.total`), nur für !isPartner überhaupt aktiv.
  const { data: toReviewCount } = useEvents(
    { status: [EventStatus.InReview], page: 1, pageSize: 1 },
    { enabled: !isPartner }
  );
  const { data: refundToReviewCount } = useEvents(
    { status: [EventStatus.Completed], refundStatus: ['in_review_proof'], page: 1, pageSize: 1 },
    { enabled: !isPartner }
  );

  // Schnellzugriff-Zähler "eigene VAs" (22.09.2026-Follow-up) — für beide
  // Rollen aktiv (ursprünglich nur Partner, jetzt auch Admin/Zentrale, siehe
  // die JSX unten). `endedBefore` statt dateTo: reiner "schon vorbei"-Schnitt,
  // keine Bereichsüberlappung (siehe EventFilters.ts).
  const todayIso = new Date().toISOString().slice(0, 10);
  const { data: toCompleteCount } = useEvents({
    status: [EventStatus.Approved],
    endedBefore: todayIso,
    page: 1,
    pageSize: 1,
  });
  const { data: refundToStartCount } = useEvents({
    status: [EventStatus.Completed],
    refundStatus: ['none'],
    page: 1,
    pageSize: 1,
  });
  const { data: refundToSubmitCount } = useEvents({
    status: [EventStatus.Completed],
    refundStatus: ['new'],
    page: 1,
    pageSize: 1,
  });

  // Ampelsystem-Schnellzugriff "Kontingent prüfen" (25.09.2026, Nutzervorgabe)
  // — für beide Rollen, dieselbe Depot-Scoping-Begründung wie beim
  // "eigene VAs"-Zähler oben. Status bewusst auf Freigegeben/Online begrenzt
  // (Nutzerentscheidung): nur dort kann noch jemand reagieren.
  const { data: lowContingentCount } = useEvents({
    status: [EventStatus.Approved, EventStatus.Online],
    lowContingent: true,
    page: 1,
    pageSize: 1,
  });

  // 401/403 means "no access", not "loading failed" — worth saying plainly, or
  // the user stares at a generic error and cannot tell it apart from an outage.
  const isDenied =
    error instanceof ApiError && (error.status === 401 || error.status === 403);

  // Too many matches to page through: the backend already phrased what to do, so
  // show that instead of the generic load error, which would read as an outage
  // when the fix is simply a narrower filter.
  const tooBroad =
    error instanceof ApiError && error.data?.error?.code === 'FILTER_TOO_BROAD'
      ? error.message
      : null;

  const createMut = useCreateEvent();
  const updateMut = useUpdateEvent();
  const deleteMut = useDeleteEvent();
  const submitMut = useSubmitEvent();
  const approveMut = useApproveEvent();
  const rejectMut = useRejectEvent();
  const reopenMut = useReopenEvent();
  const cancelMut = useCancelEvent();
  const completeMut = useCompleteEvent();
  const notifyAdvertisingMut = useNotifyAdvertisingDepots();

  // /api/me carries only depot keys (the raw JWT/assumed-role claim). Enrich
  // them with the names from /api/depots so pickers show "D001 — Köln Zentrum"
  // for partners too; falls back to the bare key while that request is pending.
  const userDepots = useMemo(() => {
    const claimed: DepotRef[] = (me?.depots ?? []).map((key) => ({ key, uuid: '' }));
    if (allDepots.length === 0) return sortDepots(claimed);

    const byKey = new Map(allDepots.map((d) => [d.key, d]));
    return sortDepots(claimed.map((d) => byKey.get(d.key) ?? d));
  }, [me?.depots, allDepots]);

  // v1: client-side depot audience for partners.
  // SEAM: real depot isolation must be enforced server-side (JWT depots claim). See PRD-018 §4.2/§4.4.
  const visibleEvents = useMemo(() => {
    const items = data?.items ?? [];
    if (!isPartner) return items;
    const keys = new Set(userDepots.map((d) => d.key));
    return items.filter((e) => e.depots.some((d) => keys.has(d.key)));
  }, [data?.items, isPartner, userDepots]);

  const openCreate = () => {
    setActiveEvent(null);
    // Clearing the copy source is what makes this a blank form: the dialog
    // prefills from copySource whenever no event is set, so a leftover source
    // from a cancelled copy would silently fill the "new event" form with it.
    setCopySource(null);
    setDialogMode('create');
    setDialogOpen(true);
  };
  const openExisting = (event: DepotEvent, mode: 'edit' | 'review') => {
    setActiveEvent(event);
    setCopySource(null);
    setDialogMode(mode);
    setDialogOpen(true);
  };

  /** Opens the create dialog prefilled from an existing event. */
  const openCopy = (event: DepotEvent) => {
    setActiveEvent(null);
    setCopySource(event);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const notifyErr = (e: unknown) =>
    toast({ title: t('toast.error'), description: e instanceof Error ? e.message : String(e), variant: 'destructive' });

  const handleDelete = async () => {
    const event = pendingDelete;
    if (!event) return;
    setPendingDelete(null);
    try {
      await deleteMut.mutateAsync(event.identifier);
      toast({ title: t('toast.deleted') });
    } catch (e) {
      notifyErr(e);
    }
  };

  const handleSaveCreate = async (payload: CreateEventRequest) => {
    try {
      await createMut.mutateAsync(payload);
      toast({ title: t('toast.created') });
      setDialogOpen(false);
    } catch (e) { notifyErr(e); }
  };
  /**
   * "Zur Prüfung" clicked while still in create mode — creates the event, then
   * submits the one just created (its identifier only exists after create()
   * resolves). Bug fix 08.09.2026: previously the dialog's submit button called
   * onSubmit() directly regardless of mode, which needs an activeEvent that
   * does not exist yet in create mode — the click silently did nothing.
   */
  const handleSubmitCreate = async (payload: CreateEventRequest) => {
    try {
      const created = await createMut.mutateAsync(payload);
      await submitMut.mutateAsync({ identifier: created.identifier });
      toast({ title: t('toast.submitted') });
      setDialogOpen(false);
    } catch (e) { notifyErr(e); }
  };
  const handleSaveUpdate = async (payload: Partial<CreateEventRequest>, keepOpen?: boolean) => {
    if (!activeEvent) return;
    try {
      await updateMut.mutateAsync({ identifier: activeEvent.identifier, ...payload });
      toast({ title: t('toast.saved') });
      // "Speichern & weiter" (keepOpen=true) blättert nur lokal weiter, statt
      // den Dialog zu schließen — Bugfix 24.09.2026, vorher schloss dieser
      // Handler ihn immer, auch wenn EventFormDialog.tsx nur zum nächsten Tab
      // wollte.
      if (!keepOpen) setDialogOpen(false);
    } catch (e) { notifyErr(e); }
  };
  /**
   * Persists whatever EventFormDialog's pendingSavePayload() computed, if
   * anything — every transition handler below runs this before its own
   * mutation (bug fix 09.09.2026: a status change used to move only the
   * status, silently dropping any field the reviewer had just edited in the
   * still-open, still-editable form).
   */
  const saveIfPending = async (payload?: Partial<CreateEventRequest>) => {
    if (!activeEvent || !payload) return;
    await updateMut.mutateAsync({ identifier: activeEvent.identifier, ...payload });
  };
  const handleSubmit = async (payload?: Partial<CreateEventRequest>) => {
    if (!activeEvent) return;
    try {
      await saveIfPending(payload);
      await submitMut.mutateAsync({ identifier: activeEvent.identifier });
      toast({ title: t('toast.submitted') });
      setDialogOpen(false);
    } catch (e) { notifyErr(e); }
  };
  const handleApprove = async (payload?: Partial<CreateEventRequest>) => {
    if (!activeEvent) return;
    try {
      await saveIfPending(payload);
      await approveMut.mutateAsync({ identifier: activeEvent.identifier });
      toast({ title: t('toast.approved') });
      setDialogOpen(false);
      // A separate confirmation, not a side effect of approval itself: the event
      // is already approved by the time this asks, so declining or a later
      // failure here must never roll it back.
      if (activeEvent.promotingDepots.length > 0) {
        setPendingAdvertisingNotify(activeEvent);
      }
    } catch (e) { notifyErr(e); }
  };
  const handleNotifyAdvertisingDepots = async () => {
    const event = pendingAdvertisingNotify;
    if (!event) return;
    setPendingAdvertisingNotify(null);
    try {
      const result = await notifyAdvertisingMut.mutateAsync(event.identifier);
      if (result.failed.length > 0) {
        toast({
          title: t('toast.advertisingNotifyPartialFailure', {
            depots: result.failed.map((f) => f.depotKey).join(', '),
          }),
          variant: 'destructive',
        });
      } else {
        toast({ title: t('toast.advertisingNotified') });
      }
    } catch (e) { notifyErr(e); }
  };
  // Zurückweisen speichert bewusst kein anhängendes Formular mehr (Nutzervorgabe,
  // 25.09.2026) — nur Status+Kommentar, kein saveIfPending().
  const handleReject = async (reason: string) => {
    if (!activeEvent) return;
    try {
      await rejectMut.mutateAsync({ identifier: activeEvent.identifier, reason });
      toast({ title: t('toast.rejected') });
      setDialogOpen(false);
    } catch (e) { notifyErr(e); }
  };
  const handleReopen = async (payload?: Partial<CreateEventRequest>) => {
    if (!activeEvent) return;
    try {
      await saveIfPending(payload);
      await reopenMut.mutateAsync({ identifier: activeEvent.identifier });
      toast({ title: t('toast.reopened') });
      setDialogOpen(false);
    } catch (e) { notifyErr(e); }
  };
  const handleCancelEvent = async (payload?: Partial<CreateEventRequest>) => {
    if (!activeEvent) return;
    try {
      await saveIfPending(payload);
      await cancelMut.mutateAsync({ identifier: activeEvent.identifier });
      toast({ title: t('toast.cancelled') });
      setDialogOpen(false);
    } catch (e) { notifyErr(e); }
  };
  const handleCompleteEvent = async (payload?: Partial<CreateEventRequest>) => {
    if (!activeEvent) return;
    try {
      await saveIfPending(payload);
      await completeMut.mutateAsync({ identifier: activeEvent.identifier });
      toast({ title: t('toast.completed') });
      setDialogOpen(false);
    } catch (e) { notifyErr(e); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('page.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('page.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          {/* Abkürzung zu den Stammdaten (Mailingdaten, Veranstaltungstexte). Am
              RECHT geprüft, nicht an der Rolle — siehe canManageEventSettings. */}
          {canManageEventSettings && (
            <Button asChild variant="outline" className="gap-2">
              <Link to="/settings">
                <Settings className="h-4 w-4" />
                {t('actions.settings')}
              </Link>
            </Button>
          )}
          {perms.canCreate && (
            <Button className="gap-2" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('actions.create')}
            </Button>
          )}
        </div>
      </div>

      <EventFilters
        filters={filters}
        depots={isPartner ? userDepots : allDepots}
        eventTypes={eventTypes}
        onChange={(f) => { setFilters(f); setCurrentPage(1); }}
        sort={sort}
        onSortChange={(s) => { setSort(s); setCurrentPage(1); }}
      />

      {/* Schnellzugriff, eine Zeile für beide Rollen (21.09./22.09.2026-Follow-up)
          — ersetzt die Filter komplett (Navigation zu genau dieser Ansicht),
          statt additiv dazuzukommen. "VA zu prüfen"/"Abrechnung zu prüfen" nur
          Admin/Zentrale (Prüfen ist keine Partner-Aktion); die übrigen drei für
          beide Rollen — bei Admin/Zentrale zeigen sie den Bestand über alle
          Depots, bei Partnern nur die eigenen (serverseitige Zugriffskontrolle
          greift unverändert). Farben: "VA zu prüfen" kräftig orange (die
          wichtigste eigene Aktion für Admin/Zentrale), alle anderen hell in der
          Farbe, die ihr Zielzustand in der "Abrechnung"-Spalte hätte (siehe
          EventDataTable.tsx's refundVariant()) — "VA abschließen" führt zu
          keinem Refund-Zustand, deshalb dasselbe Grün wie der "Online"-Status
          (19.09.2026-Follow-up), statt eines dritten Farbtons.
          "Abrechnung erstellen"/"Abrechnung abschließen" nur bei automatischer
          Abrechnung sichtbar (19.09.2026-Follow-up) — vorher, während der
          zeitlich befristeten manuellen Abrechnung (siehe settlement-mode.ts),
          würden sie nur in den serverseitigen 409 laufen. */}
      <div className="flex gap-3 pt-2">
        {!isPartner && (
          <>
            <Button
              size="sm"
              className="relative bg-orange-500 text-white hover:bg-orange-600"
              onClick={() => { setFilters({ status: [EventStatus.InReview] }); setCurrentPage(1); }}
            >
              {t('quickAccess.toReview')}
              <CountBubble count={toReviewCount?.total ?? 0} />
            </Button>
            <Button
              size="sm"
              className="relative bg-orange-100 text-orange-800 hover:bg-orange-200"
              onClick={() => {
                setFilters({ status: [EventStatus.Completed], refundStatus: ['in_review_proof'] });
                setCurrentPage(1);
              }}
            >
              {t('quickAccess.refundToReview')}
              <CountBubble count={refundToReviewCount?.total ?? 0} />
            </Button>
          </>
        )}
        <Button
          size="sm"
          className="relative bg-green-100 text-green-800 hover:bg-green-200"
          onClick={() => {
            setFilters({ status: [EventStatus.Approved], endedBefore: todayIso });
            setCurrentPage(1);
          }}
        >
          {t('quickAccess.toComplete')}
              <CountBubble count={toCompleteCount?.total ?? 0} />
        </Button>
        {me?.settlementMode === 'automatic' && (
          <>
            <Button
              size="sm"
              className="relative bg-blue-100 text-blue-700 hover:bg-blue-200"
              onClick={() => {
                setFilters({ status: [EventStatus.Completed], refundStatus: ['none'] });
                setCurrentPage(1);
              }}
            >
              {t('quickAccess.refundToStart')}
              <CountBubble count={refundToStartCount?.total ?? 0} />
            </Button>
            <Button
              size="sm"
              className="relative bg-green-100 text-green-800 hover:bg-green-200"
              onClick={() => {
                setFilters({ status: [EventStatus.Completed], refundStatus: ['new'] });
                setCurrentPage(1);
              }}
            >
              {t('quickAccess.refundToSubmit')}
              <CountBubble count={refundToSubmitCount?.total ?? 0} />
            </Button>
          </>
        )}

        {/* "Kontingent prüfen" (25.09.2026, Nutzervorgabe) — Ampelsystem-
            Schnellzugriff, für beide Rollen; kräftig orange wie "VA zu
            prüfen", weil es eine eigene, wichtige Handlungsaufforderung ist. */}
        <Button
          size="sm"
          className="relative bg-orange-500 text-white hover:bg-orange-600"
          onClick={() => {
            setFilters({ status: [EventStatus.Approved, EventStatus.Online], lowContingent: true });
            setCurrentPage(1);
          }}
        >
          {t('quickAccess.lowContingent')}
              <CountBubble count={lowContingentCount?.total ?? 0} />
        </Button>

        {/* "Filter zurücksetzen" (25.09.2026, Nutzervorgabe) — aus der
            Filterzeile in die Schnellzugriff-Zeile verschoben, rechtsbündig
            und hellrot (auch Rahmen), gleiches Farbmuster wie "Vorschau"/
            "Serie erstellen" in EventFormDialog.tsx. */}
        {hasActiveFilters && (
          <Button
            size="sm"
            className="ml-auto gap-1.5 border-red-50 bg-red-50 text-red-700 hover:border-red-100 hover:bg-red-100 hover:text-red-800"
            onClick={clearAllFilters}
          >
            <X className="h-4 w-4" />
            {t('filters.clear')}
          </Button>
        )}
      </div>


      {isLoading ? (
        <div className="p-8">
          <WineCarouselLoader label={t('page.loading')} />
        </div>
      ) : isDenied ? (
        <div className="p-8 text-center text-muted-foreground">{t('page.noAccess')}</div>
      ) : tooBroad ? (
        <div className="p-8 text-center text-amber-700">{tooBroad}</div>
      ) : isError ? (
        <div className="p-8 text-center text-red-600">{t('page.loadError')}</div>
      ) : (
        <>
          <EventDataTable
            events={visibleEvents}
            eventTypes={eventTypes}
            depots={allDepots}
            canEdit={perms.canEdit}
            canDelete={perms.canDelete}
            canCreate={perms.canCreate}
            isCentral={!isPartner}
            isAdmin={role === Role.Admin}
            onOpen={openExisting}
            onDelete={setPendingDelete}
            onCopy={openCopy}
            onOpenRefund={setRefundFor}
            onOpenParticipants={setParticipantsFor}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={data?.totalPages ?? 1}
            pageSize={pageSize}
            totalItems={data?.total ?? 0}
            onPageChange={(p) => {
              setCurrentPage(p);
              // Sonst bleibt der Scroll-Stand der vorigen Seite stehen — bei einer
              // langen Tabelle sieht man dann erstmal nichts vom neuen Inhalt
              // (22.09.2026-Follow-up).
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
          />
        </>
      )}

      <EventFormDialog
        open={dialogOpen}
        mode={dialogMode}
        event={activeEvent}
        copySource={copySource}
        role={role}
        userDepots={userDepots}
        allDepots={allDepots}
        eventTypes={eventTypes}
        onOpenChange={setDialogOpen}
        onSaveCreate={handleSaveCreate}
        onSaveUpdate={handleSaveUpdate}
        onSubmit={handleSubmit}
        onSubmitCreate={handleSubmitCreate}
        onApprove={handleApprove}
        onReject={handleReject}
        onReopen={handleReopen}
        onCancelEvent={handleCancelEvent}
        onCompleteEvent={handleCompleteEvent}
        onOpenRefund={
          activeEvent
            ? () => {
                setDialogOpen(false);
                setRefundFor(activeEvent);
              }
            : undefined
        }
        onDelete={
          // Same rule as the table: a partner loses the option once the event is
          // submitted, and (19.09.2026-Follow-up) never gets it for a central-
          // origin event, even while still Initial/Rejected (24.09.2026, same
          // level) — enforced server-side too. Passing undefined hides the
          // button entirely.
          activeEvent &&
          perms.canDelete &&
          (!isPartner ||
            ((activeEvent.status === EventStatus.Initial ||
              activeEvent.status === EventStatus.Rejected) &&
              activeEvent.origin !== EventOrigin.Central))
            ? () => setPendingDelete(activeEvent)
            : undefined
        }
      />

      <RefundWizardDialog
        event={refundFor}
        onOpenChange={(open) => !open && setRefundFor(null)}
      />

      <ParticipantsDialog
        event={participantsFor}
        onOpenChange={(open) => !open && setParticipantsFor(null)}
      />

      {/* Deleting takes the ticket contingent with it, so it asks first. */}
      <ConfirmationDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t('form.deleteTitle')}
        description={t('form.deleteDescription', { title: pendingDelete?.title ?? '' })}
        confirmLabel={t('actions.deleteConfirm')}
        cancelLabel={t('actions.cancel')}
        variant="destructive"
        onConfirm={handleDelete}
      />

      <ConfirmationDialog
        open={pendingAdvertisingNotify !== null}
        onOpenChange={(open) => !open && setPendingAdvertisingNotify(null)}
        title={t('form.notifyAdvertising.title')}
        description={t('form.notifyAdvertising.description', {
          count: pendingAdvertisingNotify?.promotingDepots.length ?? 0,
        })}
        confirmLabel={t('form.notifyAdvertising.confirm')}
        cancelLabel={t('actions.cancel')}
        onConfirm={handleNotifyAdvertisingDepots}
      />
    </div>
  );
}
